import { IRawStyle } from '../styling';

import { getGutterValue } from './gutters';

export type PaddingPropName =
  /**
   * Number of gutter units to be added as padding within this block.
   */
  | 'padding'
  /**
   * Number of gutter units to be added as top and bottom padding. Will override a "padding" value for top and bottom.
   */
  | 'verticalPadding'
  /**
   * Number of gutter units to be added as left and right padding. Will override a "padding" value for left and right.
   */
  | 'horizontalPadding'
  /**
   * Number of gutter units to be added as top padding. Will override `padding` and `verticalPadding` props.
   */
  | 'paddingTop'
  /**
   * Number of gutter units to be added as right padding. Will override `padding` and `horizontalPadding` props.
   */
  | 'paddingRight'
  /**
   * Number of gutter units to be added as bottom padding. Will override `padding` and `verticalPadding` props.
   */
  | 'paddingBottom'
  /**
   * Number of gutter units to be added as left padding. Will override `padding` and `horizontalPadding` props.
   */
  | 'paddingLeft';

export type PaddingProps = {
  [key in PaddingPropName]?: number;
};

const hasValue = (value: number | undefined) => value != null;
const getFirstDefinedValue = (value1: number | undefined, value2: number | undefined, value3: number | undefined) => {
  if (hasValue(value1)) {
    return value1;
  }

  if (hasValue(value2)) {
    return value2;
  }

  return value3;
};

type GetPaddingStyles = (styles: PaddingProps) => IRawStyle;
export const getPaddingStyles: GetPaddingStyles = (styles) => {
  const top = getFirstDefinedValue(styles.paddingTop, styles.verticalPadding, styles.padding);
  const bottom = getFirstDefinedValue(styles.paddingBottom, styles.verticalPadding, styles.padding);
  const right = getFirstDefinedValue(styles.paddingRight, styles.horizontalPadding, styles.padding);
  const left = getFirstDefinedValue(styles.paddingLeft, styles.horizontalPadding, styles.padding);

  return {
    paddingTop: top ? getGutterValue(top) : undefined,
    paddingBottom: bottom ? getGutterValue(bottom) : undefined,
    paddingLeft: left ? getGutterValue(left) : undefined,
    paddingRight: right ? getGutterValue(right) : undefined,
  };
};
